import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import MoreVert from '@mui/icons-material/MoreVert';
import Visibility from '@mui/icons-material/Visibility';
import Download from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import { fetchPartnerships, deletePartnership } from '../../store/slices/partnershipSlice';
import { getPartnershipTypeLabel, formatDateFr } from '../../utils/partnershipUtils';

function ContractActions({ contract }) {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  if (!contract?.url) return <Typography variant="body2" color="text.secondary">—</Typography>;

  const isImage = contract.mimetype?.startsWith('image/') || contract.resourceType === 'image';

  const handleView = () => {
    if (isImage) {
      setImageDialogOpen(true);
    } else {
      window.open(contract.url, '_blank');
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={handleView} title="Visualiser">
          <Visibility fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          component="a"
          href={contract.url}
          download={contract.filename || 'document'}
          target="_blank"
          rel="noopener noreferrer"
          title="Télécharger"
        >
          <Download fontSize="small" />
        </IconButton>
      </Box>
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Contrat</DialogTitle>
        <DialogContent>
          <Box component="img" src={contract.url} alt="Contrat" sx={{ maxWidth: '100%', height: 'auto' }} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function PartnershipListContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { list: items, loading } = useSelector((s) => s.partnership);
  const [typeFilter, setTypeFilter] = useState('all');
  const [endFilter, setEndFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  useEffect(() => {
    dispatch(
      fetchPartnerships({
        partnershipType: typeFilter !== 'all' ? typeFilter : undefined,
        hasEndDate: endFilter === 'all' ? undefined : endFilter === 'yes' ? 'true' : 'false',
        search: searchQuery.trim() || undefined,
      })
    );
  }, [dispatch, typeFilter, endFilter, searchQuery]);

  const handleDelete = async (id) => {
    setMenuAnchor(null);
    if (!window.confirm('Supprimer ce partenariat ?')) return;
    setActionLoadingId(id);
    const result = await dispatch(deletePartnership(id));
    setActionLoadingId(null);
    if (deletePartnership.fulfilled.match(result)) {
      enqueueSnackbar('Partenariat supprimé.', { variant: 'success' });
    } else {
      enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Partenariats
        </Typography>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Add />}
          onClick={() => navigate('/dashboard/partnerships/new')}
          disabled={loading}
        >
          Créer
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Rechercher par nom…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 140 }}
        >
          <option value="all">Tous</option>
          <option value="academic">{getPartnershipTypeLabel('academic')}</option>
          <option value="research">{getPartnershipTypeLabel('research')}</option>
          <option value="industry">{getPartnershipTypeLabel('industry')}</option>
          <option value="other">{getPartnershipTypeLabel('other')}</option>
        </TextField>
        <TextField
          select
          size="small"
          label="Date de fin"
          value={endFilter}
          onChange={(e) => setEndFilter(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 140 }}
        >
          <option value="all">Tous</option>
          <option value="yes">Avec fin</option>
          <option value="no">Sans fin</option>
        </TextField>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table size="small" sx={{ minWidth: isMobile ? 700 : 'auto' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Nom</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Début</TableCell>
              <TableCell>Fin</TableCell>
              <TableCell>Contrat</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Aucun partenariat.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{getPartnershipTypeLabel(item.partnershipType)}</TableCell>
                  <TableCell>{formatDateFr(item.startDate)}</TableCell>
                  <TableCell>{formatDateFr(item.endDate)}</TableCell>
                  <TableCell>
                    <ContractActions contract={item.contract} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/dashboard/partnerships/edit/${item._id}`)}
                      disabled={!!actionLoadingId}
                      title="Modifier"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setSelectedItem(item);
                      }}
                      disabled={!!actionLoadingId}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => selectedItem && handleDelete(selectedItem._id)}
          disabled={!!actionLoadingId}
        >
          {actionLoadingId === selectedItem?._id ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Supprimer
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default function PartnershipList() {
  const { user } = useSelector((s) => s.auth);
  const allowed = ['admin', 'moderator'].includes(user?.role);
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <PartnershipListContent />;
}
